import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Filter, Plus, User, ChevronRight, MoreVertical, Edit, UserPlus, Trash2, Eye, X, Users } from 'lucide-react';
import { parentManagement, parentAssignments } from '../services/api';

const ParentList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [parentsList, setParentsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchParents = async () => {
    try {
      setLoading(true);
      console.log('Fetching parents...');
      const response = await parentManagement.getAllParents();
      console.log('Parents response:', response);
      
      const parentsData = response.data.data;
      setParentsList(parentsData);

      // Get current user info from the response if available
      if (response.data.user) {
        setCurrentUser(response.data.user);
      }
    } catch (error) {
      console.error('Error fetching parents:', error);
      setError('Falha ao carregar lista de pais');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParents();
  }, []);

  // Refresh parents list when navigated with refresh flag
  useEffect(() => {
    if (location.state?.refresh) {
      fetchParents();
      // Clear the refresh flag
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.refresh, navigate]);

  // Filter parents based on search term and status
  const filteredParents = parentsList.filter(parent => {
    const matchesSearch = 
      (parent.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (parent.lastName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (parent.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || parent.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleAddParent = () => {
    navigate('/dashboard/parents/new');
  };

  const handleViewParent = (parentId) => {
    if (!parentId) {
      console.error('No parent ID provided');
      return;
    }
    console.log('Navigating to parent:', parentId);
    navigate(`/dashboard/parents/${parentId}`);
  };

  const handleEditParent = (parentId) => {
    navigate(`/dashboard/parents/${parentId}/edit`);
  };

  const handleDeleteParent = (parent) => {
    setSelectedParent(parent);
    setShowDeleteConfirm(true);
    setShowActionsMenu(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedParent) return;

    try {
      setLoading(true);
      await parentManagement.deleteParent(selectedParent.id);
      setSuccess('Responsável removido com sucesso');
      setShowDeleteConfirm(false);
      setSelectedParent(null);
      fetchParents();
    } catch (error) {
      console.error('Error deleting parent:', error);
      setError('Falha ao remover responsável');
    } finally {
      setLoading(false);
    }
  };

  const handleManageAssignments = (parentId) => {
    navigate(`/dashboard/parents/${parentId}/assignments`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Ativo';
      case 'inactive':
        return 'Inativo';
      case 'suspended':
        return 'Suspenso';
      default:
        return status;
    }
  };

  if (loading && parentsList.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciar Responsáveis</h1>
          <p className="text-gray-600">Lista de pais cadastrados no sistema</p>
        </div>
        <button
          onClick={handleAddParent}
          className="bg-brand-700 text-white px-4 py-2 rounded-lg hover:bg-brand-800 flex items-center gap-2"
        >
          <UserPlus className="w-4 h-4" />
          Adicionar Responsável
        </button>
      </div>

      {/* Notifications */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 text-red-800 border border-red-200 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 text-green-800 border border-green-200 rounded-lg">
          {success}
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar pais..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            >
              <option value="all">Todos os status</option>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="suspended">Suspenso</option>
            </select>
          </div>
        </div>
      </div>

      {/* Parents List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {filteredParents.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum responsável encontrado</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || filterStatus !== 'all' 
                ? 'Tente ajustar os filtros de busca'
                : 'Comece adicionando o primeiro responsável'
              }
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <button
                onClick={handleAddParent}
                className="bg-brand-700 text-white px-4 py-2 rounded-lg hover:bg-brand-800"
              >
                Adicionar Primeiro Responsável
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Responsável
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Crianças
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Último Login
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredParents.map((parent) => (
                  <tr key={parent.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-brand-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-brand-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {parent.firstName} {parent.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {parent.id.slice(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{parent.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(parent.status)}`}>
                        {getStatusText(parent.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {parent.childrenCount || 0} criança(s)
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {parent.lastLogin 
                        ? new Date(parent.lastLogin).toLocaleDateString('pt-BR')
                        : 'Nunca'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="relative">
                        <button
                          onClick={() => setShowActionsMenu(showActionsMenu === parent.id ? null : parent.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        
                        {showActionsMenu === parent.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                            <div className="py-1">
                              <button
                                onClick={() => handleViewParent(parent.id)}
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Ver Detalhes
                              </button>
                              <button
                                onClick={() => handleEditParent(parent.id)}
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Editar
                              </button>
                              <button
                                onClick={() => handleManageAssignments(parent.id)}
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                              >
                                <Users className="w-4 h-4 mr-2" />
                                Gerenciar Crianças
                              </button>
                              <button
                                onClick={() => handleDeleteParent(parent)}
                                className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Remover
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Confirmar Remoção
            </h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja remover o responsável "{selectedParent?.firstName} {selectedParent?.lastName}"? 
              Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedParent(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Remover
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentList; 